// Client-side terrain-elevation computation.
//
// Browser Geolocation returns null/inaccurate `coords.altitude` on most devices, so we
// recompute real terrain elevation for a GPS route from a free elevation API. Doing this
// in the BROWSER (instead of only on the backend) means it works even when the deployed
// backend's outbound network is restricted — the user's device has normal internet access.
//
// The result is sent with the run; the backend keeps its own server-side computation as a
// fallback (e.g. for Strava-imported runs that never touch this code path).

const M_TO_FT = 3.28084;
const OPEN_METEO_URL = "https://api.open-meteo.com/v1/elevation";

function downsample(coords, maxPoints = 100) {
  const n = coords.length;
  if (n <= maxPoints) return coords;
  const step = n / maxPoints;
  const idxs = new Set();
  for (let i = 0; i < maxPoints; i++) idxs.add(Math.min(Math.floor(i * step), n - 1));
  idxs.add(n - 1);
  return [...idxs].sort((a, b) => a - b).map((i) => coords[i]);
}

function haversineMi(a, b) {
  const R = 3958.7613; // Earth radius in miles
  const toRad = (d) => (d * Math.PI) / 180;
  const [lon1, lat1, lon2, lat2] = [a[0], a[1], b[0], b[1]].map(toRad);
  const dlon = lon2 - lon1;
  const dlat = lat2 - lat1;
  const h =
    Math.sin(dlat / 2) ** 2 +
    Math.cos(lat1) * Math.cos(lat2) * Math.sin(dlon / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(h));
}

async function fetchOpenMeteo(pts) {
  const lat = pts.map((c) => c[1].toFixed(5)).join(",");
  const lng = pts.map((c) => c[0].toFixed(5)).join(",");
  const res = await fetch(`${OPEN_METEO_URL}?latitude=${lat}&longitude=${lng}`);
  if (!res.ok) throw new Error(`open-meteo ${res.status}`);
  const data = await res.json();
  const elevs = data.elevation;
  if (!elevs || elevs.length !== pts.length) throw new Error("open-meteo bad payload");
  return elevs;
}

/**
 * Compute terrain elevation gain + profile for a GPS route, entirely in the browser.
 * @param {Array<[number, number]>} coordinates GeoJSON [lng, lat] pairs.
 * @param {number} [distanceMi] total run distance, used to scale the profile x-axis.
 * @returns {Promise<{gain_ft:number, profile:Array<{dist_mi:number, elev_ft:number}>}|null>}
 */
export async function computeElevation(coordinates, distanceMi) {
  try {
    if (!Array.isArray(coordinates) || coordinates.length < 2) return null;
    const pts = downsample(coordinates, 100);

    let elevs = null;
    try {
      elevs = await fetchOpenMeteo(pts);
    } catch (e) {
      console.warn("[elevation] open-meteo failed:", e.message);
    }
    if (!elevs || !elevs.some((e) => e != null)) return null;

    // Cumulative distance (miles) along the sampled points.
    const cum = [0];
    for (let i = 1; i < pts.length; i++) cum.push(cum[i - 1] + haversineMi(pts[i - 1], pts[i]));
    const rawTotal = cum[cum.length - 1];
    let scaled = cum;
    if (distanceMi && rawTotal > 0) {
      const scale = distanceMi / rawTotal;
      scaled = cum.map((c) => c * scale);
    }

    let gainM = 0;
    let prev = null;
    const profile = [];
    for (let i = 0; i < elevs.length; i++) {
      const e = elevs[i];
      if (e == null) continue;
      profile.push({
        dist_mi: Math.round(scaled[i] * 1000) / 1000,
        elev_ft: Math.round(e * M_TO_FT * 10) / 10,
      });
      if (prev != null && e > prev) gainM += e - prev;
      prev = e;
    }
    if (profile.length < 2) return null;
    return { gain_ft: Math.round(gainM * M_TO_FT), profile };
  } catch (e) {
    console.warn("[elevation] compute failed:", e);
    return null;
  }
}
